import apiClient from "./client";
import type {
  Brand,
  BrandCreate,
  BrandUpdate,
  BlogPost,
  BlogPostCreate,
  BlogPostUpdate,
  ContactMessage,
  DealerApplication,
  NewsletterSubscriber,
  JobListing,
  JobListingCreate,
  JobListingUpdate,
  SocialProject,
  SocialProjectCreate,
  SocialProjectUpdate,
  PageContent,
  SeoMeta,
  SeoUpsert,
  Media,
  ProductGroup,
} from "@endeks/shared";

// ── Auth ──────────────────────────────────────────────────────────────────────

type AuthUser = { id: string; name: string; email: string; role: string };
type AuthTokenResponse = {
  access_token: string;
  refresh_token: string;
  user: AuthUser;
};

export const authApi = {
  login: (data: { email: string; password: string }) =>
    apiClient.post<AuthTokenResponse>("/api/auth/login", data),
};

// ── Brands ────────────────────────────────────────────────────────────────────

export const brandsApi = {
  list: (search?: string) => apiClient.get<Brand[]>("/api/brands", { params: { search } }),
  get: (id: string) => apiClient.get<Brand>(`/api/brands/${id}`),
  create: (data: BrandCreate) => apiClient.post<Brand>("/api/brands", data),
  update: (id: string, data: Omit<BrandUpdate, "id">) =>
    apiClient.put<Brand>(`/api/brands/${id}`, data),
  delete: (id: string) => apiClient.delete<Brand>(`/api/brands/${id}`),
  reorder: (id: string, order: number) =>
    apiClient.put<Brand>(`/api/brands/${id}/reorder`, { order }),
  reorderAll: (items: { id: string; order: number }[]) =>
    apiClient.put<{ updated: number }>("/api/brands/reorder", { items }),
};

// ── Blog ──────────────────────────────────────────────────────────────────────

export const blogApi = {
  // Admin listesi taslakları da içerir; public "/api/blog" yalnızca yayındakileri döner.
  list: (params?: { search?: string; published?: boolean; brand?: string }) =>
    apiClient.get<BlogPost[]>("/api/blog/all", { params }),
  get: (id: string) => apiClient.get<BlogPost>(`/api/blog/${id}`),
  create: (data: BlogPostCreate) => apiClient.post<BlogPost>("/api/blog", data),
  update: (id: string, data: Omit<BlogPostUpdate, "id">) =>
    apiClient.put<BlogPost>(`/api/blog/${id}`, data),
  delete: (id: string) => apiClient.delete<BlogPost>(`/api/blog/${id}`),
};

// ── Contacts ──────────────────────────────────────────────────────────────────

export const contactsApi = {
  list: (params?: { search?: string; type?: string; read?: boolean }) =>
    apiClient.get<ContactMessage[]>("/api/contacts", { params }),
  stats: () => apiClient.get<{ total: number; unread: number }>("/api/contacts/stats"),
  get: (id: string) => apiClient.get<ContactMessage>(`/api/contacts/${id}`),
  markRead: (id: string) => apiClient.put<ContactMessage>(`/api/contacts/${id}/read`),
  markAllRead: (ids: string[]) =>
    apiClient.put<{ count: number }>("/api/contacts/bulk-read", { ids }),
  delete: (id: string) => apiClient.delete<ContactMessage>(`/api/contacts/${id}`),
};

// ── Dealers ───────────────────────────────────────────────────────────────────

export const dealersApi = {
  list: (params?: { search?: string; status?: string }) =>
    apiClient.get<DealerApplication[]>("/api/dealers", { params }),
  stats: () => apiClient.get<{ total: number; pending: number }>("/api/dealers/stats"),
  get: (id: string) => apiClient.get<DealerApplication>(`/api/dealers/${id}`),
  updateStatus: (id: string, status: string) =>
    apiClient.put<DealerApplication>(`/api/dealers/${id}/status`, { status }),
  markRead: (id: string) => apiClient.put<DealerApplication>(`/api/dealers/${id}/read`),
  delete: (id: string) => apiClient.delete<DealerApplication>(`/api/dealers/${id}`),
};

// ── Newsletter ────────────────────────────────────────────────────────────────

export const newsletterApi = {
  list: (search?: string) =>
    apiClient.get<NewsletterSubscriber[]>("/api/newsletter", { params: { search } }),
  stats: () => apiClient.get<{ total: number; active: number }>("/api/newsletter/stats"),
  delete: (id: string) => apiClient.delete<NewsletterSubscriber>(`/api/newsletter/${id}`),
};

// ── Jobs ──────────────────────────────────────────────────────────────────────

export const jobsApi = {
  // Admin listesi pasif ilanları da içerir; public "/api/jobs" yalnızca aktifleri döner.
  list: (active?: boolean) => apiClient.get<JobListing[]>("/api/jobs/all", { params: { active } }),
  get: (id: string) => apiClient.get<JobListing>(`/api/jobs/${id}`),
  create: (data: JobListingCreate) => apiClient.post<JobListing>("/api/jobs", data),
  update: (id: string, data: Omit<JobListingUpdate, "id">) =>
    apiClient.put<JobListing>(`/api/jobs/${id}`, data),
  delete: (id: string) => apiClient.delete<JobListing>(`/api/jobs/${id}`),
};

// ── Social Projects ───────────────────────────────────────────────────────────

export const socialProjectsApi = {
  list: () => apiClient.get<SocialProject[]>("/api/social-projects/all"),
  get: (id: string) => apiClient.get<SocialProject>(`/api/social-projects/${id}`),
  getBySlug: (slug: string) =>
    apiClient.get<SocialProject>(`/api/social-projects/slug/${slug}`),
  create: (data: SocialProjectCreate) =>
    apiClient.post<SocialProject>("/api/social-projects", data),
  update: (id: string, data: Omit<SocialProjectUpdate, "id">) =>
    apiClient.put<SocialProject>(`/api/social-projects/${id}`, data),
  delete: (id: string) => apiClient.delete<SocialProject>(`/api/social-projects/${id}`),
  reorder: (id: string, order: number) =>
    apiClient.put<SocialProject>(`/api/social-projects/${id}/reorder`, { order }),
  reorderAll: (items: { id: string; order: number }[]) =>
    apiClient.put<{ updated: number }>("/api/social-projects/reorder", { items }),
};

// ── Product Groups ────────────────────────────────────────────────────────────

export const productGroupsApi = {
  list: () => apiClient.get<ProductGroup[]>("/api/product-groups"),
  create: (data: { nameTr: string; nameEn?: string }) =>
    apiClient.post<ProductGroup>("/api/product-groups", data),
  update: (id: string, data: { nameTr?: string; nameEn?: string }) =>
    apiClient.put<ProductGroup>(`/api/product-groups/${id}`, data),
  delete: (id: string) => apiClient.delete<ProductGroup>(`/api/product-groups/${id}`),
};

// ── Page Content ──────────────────────────────────────────────────────────────

/** Namespace summary returned by GET /api/page-content/admin (list endpoint). */
export type ContentNamespace = {
  page: string;
  label: string;
  group: string;
  locales: string[];
};

export const contentApi = {
  /** Returns namespace summaries — NOT PageContent rows. */
  adminList: () => apiClient.get<ContentNamespace[]>("/api/page-content/admin"),
  /** Returns the raw key→value data for a specific page+locale. */
  getEntry: (page: string, locale: string) =>
    apiClient.get<{ page: string; locale: string; data: Record<string, string> }>(
      `/api/page-content/admin/${page}/${locale}`,
    ),
  upsert: (page: string, locale: string, data: Record<string, string>) =>
    apiClient.put<PageContent>(`/api/page-content/admin/${page}/${locale}`, { data }),
};

// ── SEO ───────────────────────────────────────────────────────────────────────

export const seoApi = {
  get: (path: string, locale: string) =>
    apiClient.get<SeoMeta | null>("/api/seo", { params: { path, locale } }),
  listAdmin: () => apiClient.get<SeoMeta[]>("/api/seo/admin"),
  upsert: (data: SeoUpsert) => apiClient.put<SeoMeta>("/api/seo/admin", data),
};

// ── Media ─────────────────────────────────────────────────────────────────────

/** Paginated response from GET /api/media (media.service.ts findAll) */
export type MediaListResponse = {
  items: Media[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export const mediaApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    mimeType?: string;
    folder?: string;
  }) => apiClient.get<MediaListResponse>("/api/media", { params }),
  get: (id: string) => apiClient.get<Media>(`/api/media/${id}`),
  upload: (file: File, alt?: string, folder?: string) => {
    const formData = new FormData();
    formData.append("file", file);
    if (alt) formData.append("alt", alt);
    if (folder) formData.append("folder", folder);
    return apiClient.post<Media>("/api/media/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadMultiple: (files: File[], folder?: string) => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    if (folder) formData.append("folder", folder);
    return apiClient.post<Media[]>("/api/media/upload/multiple", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  update: (id: string, data: { alt?: string }) =>
    apiClient.put<Media>(`/api/media/${id}`, data),
  delete: (id: string) => apiClient.delete<Media>(`/api/media/${id}`),
};

export interface MailRecipients {
  contact: string;
  dealer: string;
  jobs: string;
}

type Bilingual = { tr: string; en: string };

export interface FooterConfig {
  address: Bilingual;
  phone: string;
  email: string;
  social: { facebook: string; instagram: string; linkedin: string };
  workingHours: Bilingual;
  copyright: Bilingual;
  holdingLabel: string;
  holdingUrl: string;
}

export const settingsApi = {
  getRecipients: () => apiClient.get<MailRecipients>("/api/settings/recipients"),
  setRecipients: (data: MailRecipients) =>
    apiClient.put<{ success: boolean }>("/api/settings/recipients", data),
  getFooter: () => apiClient.get<FooterConfig>("/api/settings/footer"),
  setFooter: (data: FooterConfig) => apiClient.put<FooterConfig>("/api/settings/footer", data),
};

export interface JobApplicationRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  jobTitle: string;
  coverLetter: string;
  cvUrl: string | null;
  createdAt: string;
}

export const jobApplicationsApi = {
  list: () => apiClient.get<JobApplicationRow[]>("/api/jobs/applications"),
  get: (id: string) => apiClient.get<JobApplicationRow>(`/api/jobs/applications/${id}`),
  delete: (id: string) => apiClient.delete<{ success: boolean }>(`/api/jobs/applications/${id}`),
};
